// Harmony: turn ONE pressed key into several notes.
//
// Pure and DOM-free (like the rest of `engine/`), so the same rules can drive
// the game/hardware players later. Everything in and out is a MIDI number;
// fractional values are allowed and meaningful — the harmonic series lands
// between the piano keys on purpose, and `midiToFreq` handles floats.
//
// Three modes:
//   note  — pass the key straight through (the default, unchanged behaviour)
//   chord — stack notes on the key, either locked to a scale or as a fixed shape
//   harm  — stack the natural overtones of the key (2×, 3×, 4× … the frequency)

export type HarmonyMode = "note" | "chord" | "harm";

/** Arpeggio speed as a musical division of the beat. "off" = strum instead. */
export type ArpRate = "off" | "1/4" | "1/8" | "1/8t" | "1/16";
/** Which way the arpeggio walks the notes. */
export type ArpDir = "up" | "down" | "updown";

/** Scales auto-pick the chord that belongs on each step, so you can't play a
 *  wrong one. Shapes stack the same fixed interval set on every key. */
export type ChordChoice =
  | "scale:major"
  | "scale:minor"
  | "scale:pentatonic"
  | "shape:maj"
  | "shape:min"
  | "shape:dom7"
  | "shape:sus2"
  | "shape:sus4"
  | "shape:power"
  | "shape:octave";

export interface HarmonySettings {
  mode: HarmonyMode;
  /** Key centre as a pitch class 0..11 (0 = C). Only used by `scale:*`. */
  root: number;
  chord: ChordChoice;
  /** Stack a 7th — four notes instead of three. `scale:*` only: the extra note
   *  is just one more step of the same scale, so every degree gets the seventh
   *  chord that actually belongs there. `shape:dom7` is unaffected. */
  seventh: boolean;
  /** Inversion: rotate the lowest note(s) up an octave (or the highest down). */
  shift: number;
  /** Milliseconds between successive notes, so a chord sounds played.
   *  Ignored while an arpeggio is running — they're alternatives. */
  strumMs: number;
  /** Arpeggio: instead of firing the notes once, repeat them in time with the
   *  project BPM for as long as the key is held. "off" = plain strum. */
  arp: ArpRate;
  arpDir: ArpDir;
  /** How many overtones in `harm` mode. */
  partials: number;
  /** Odd partials only — hollow, clarinet/square-ish. */
  oddOnly: boolean;
}

export const DEFAULT_HARMONY: HarmonySettings = {
  mode: "note",
  root: 0,
  // Pentatonic has no semitone clashes, so it's the hardest scale to make sound
  // bad — the right default for exploring. 12 ms of strum stops a chord landing
  // as one flat stamp; both defaults are Victor's call after playing it.
  chord: "scale:pentatonic",
  seventh: false,
  shift: 0,
  strumMs: 12,
  arp: "off",
  arpDir: "up",
  partials: 4,
  oddOnly: false,
};

const SCALES: Record<string, readonly number[]> = {
  major: [0, 2, 4, 5, 7, 9, 11],
  minor: [0, 2, 3, 5, 7, 8, 10], // natural minor
  pentatonic: [0, 2, 4, 7, 9],
};

const SHAPES: Record<string, readonly number[]> = {
  maj: [0, 4, 7],
  min: [0, 3, 7],
  dom7: [0, 4, 7, 10],
  sus2: [0, 2, 7],
  sus4: [0, 5, 7],
  power: [0, 7],
  octave: [0, 12],
};

export const CHORD_LABELS: ReadonlyArray<{ value: ChordChoice; label: string; group: string }> = [
  { value: "scale:major", label: "Major", group: "In key" },
  { value: "scale:minor", label: "Minor", group: "In key" },
  { value: "scale:pentatonic", label: "Pentatonic", group: "In key" },
  { value: "shape:maj", label: "Maj", group: "Fixed shape" },
  { value: "shape:min", label: "Min", group: "Fixed shape" },
  { value: "shape:dom7", label: "7th", group: "Fixed shape" },
  { value: "shape:sus2", label: "sus2", group: "Fixed shape" },
  { value: "shape:sus4", label: "sus4", group: "Fixed shape" },
  { value: "shape:power", label: "Power 5", group: "Fixed shape" },
  { value: "shape:octave", label: "Octave", group: "Fixed shape" },
];

/** Arpeggio rates as fractions of a beat. */
const ARP_BEATS: Record<ArpRate, number> = {
  off: 0,
  "1/4": 1,
  "1/8": 0.5,
  "1/8t": 1 / 3,
  "1/16": 0.25,
};
/** Each note sounds for most of its step, leaving a small gap so the steps
 *  articulate instead of smearing into one held chord. */
const ARP_GATE = 0.85;

export const ARP_RATE_LABELS: ReadonlyArray<{ value: ArpRate; label: string }> = [
  { value: "off", label: "Off" },
  { value: "1/4", label: "1/4" },
  { value: "1/8", label: "1/8" },
  { value: "1/8t", label: "1/8T" },
  { value: "1/16", label: "1/16" },
];

export const ARP_DIR_LABELS: ReadonlyArray<{ value: ArpDir; label: string }> = [
  { value: "up", label: "Up" },
  { value: "down", label: "Down" },
  { value: "updown", label: "Up-down" },
];

export const NOTE_NAMES: readonly string[] = [
  "C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B",
];

/** Snap a key press onto the nearest note of the scale, and report which step
 *  of the scale that is — the step is what decides the chord's flavour. */
function snapToScale(midi: number, root: number, scale: readonly number[]): { degree: number; octave: number } {
  const rel = midi - root;
  const octave = Math.floor(rel / 12);
  const pc = rel - octave * 12;
  let best = 0;
  let bestDist = Infinity;
  for (let i = 0; i < scale.length; i++) {
    const d = Math.abs(scale[i] - pc);
    if (d < bestDist) {
      bestDist = d;
      best = i;
    }
  }
  return { degree: best, octave };
}

/** Absolute MIDI for a scale step, letting `degree` run past the end (it wraps
 *  into the next octave) — that's what stacking thirds inside a scale means. */
function scaleNote(root: number, scale: readonly number[], degree: number, octave: number): number {
  const n = scale.length;
  const wrap = Math.floor(degree / n);
  const idx = degree - wrap * n;
  return root + (octave + wrap) * 12 + scale[idx];
}

/** Inversion. Positive lifts the lowest note(s) an octave, negative drops the
 *  highest — same chord, different colour and height. */
function invert(notes: number[], shift: number): number[] {
  const out = [...notes].sort((a, b) => a - b);
  for (let i = 0; i < shift && out.length > 1; i++) out.push(out.shift()! + 12);
  for (let i = 0; i < -shift && out.length > 1; i++) out.unshift(out.pop()! - 12);
  return out;
}

/**
 * Expand one pressed key into the notes that should actually sound.
 * Always returns at least one note. Order is low→high (strum order).
 */
export function expandNotes(midi: number, s: HarmonySettings): number[] {
  if (s.mode === "note") return [midi];

  if (s.mode === "harm") {
    // Partial n sits 12·log2(n) semitones above the fundamental. Partial 7 is
    // audibly "between" the piano notes — that's the point, not a rounding bug.
    const out: number[] = [];
    const count = Math.max(1, Math.round(s.partials));
    for (let i = 0, n = 1; out.length < count; i++, n++) {
      if (s.oddOnly && n % 2 === 0) continue;
      out.push(midi + 12 * Math.log2(n));
    }
    return clampAll(out);
  }

  // --- chord ---
  const [kind, name] = s.chord.split(":");
  let notes: number[];
  if (kind === "scale") {
    const scale = SCALES[name] ?? SCALES.major;
    const { degree, octave } = snapToScale(midi, s.root, scale);
    // Stack thirds *within the scale* — that's what makes step 1 major, step 2
    // minor, and so on, without the player having to know any of it. One more
    // step (+6) is the seventh, and it stays correct per degree for the same
    // reason: in C major that's Imaj7 ii-7 iii-7 IVmaj7 V7 vi-7 vii-7b5.
    // Pentatonic has only 5 notes, so +6 wraps to the scale's 2nd an octave up
    // — a 9th rather than a 7th. That's deliberate: it gives the 6/9 and
    // quartal voicings pentatonic music actually uses, and (like the rest of
    // the scale) it can't produce a semitone clash.
    const steps = s.seventh ? [0, 2, 4, 6] : [0, 2, 4];
    notes = steps.map((d) => scaleNote(s.root, scale, degree + d, octave));
  } else {
    const shape = SHAPES[name] ?? SHAPES.maj;
    notes = shape.map((iv) => midi + iv);
  }
  return clampAll(invert(notes, Math.round(s.shift)));
}

/** Keep everything inside MIDI range; drop anything that falls off the end. */
function clampAll(notes: number[]): number[] {
  const out = notes.filter((n) => n >= 0 && n <= 127);
  return out.length ? out : [Math.min(127, Math.max(0, notes[0] ?? 60))];
}

// ---- Arpeggio ------------------------------------------------------------
// This module stays pure: it owns no timers and never reads a clock. It only
// *describes* the pattern — which notes, in what order, how far apart — and the
// caller (main.ts) does the scheduling, exactly as it already does for strum.

export interface ArpPlan {
  /** One full cycle, in trigger order. Repeat it: `order[step % order.length]`. */
  order: number[];
  /** Seconds between successive triggers, at the BPM passed in. */
  stepSec: number;
  /** How long one note should sound. Always < `stepSec`, so steps articulate. */
  gateSec: number;
}

/** True when a key press should arpeggiate rather than strum. */
export function arpEnabled(s: HarmonySettings): boolean {
  return s.mode !== "note" && s.arp !== "off";
}

/**
 * Turn the notes of one press into a repeating pattern. Returns `null` when
 * there's nothing to arpeggiate — in that case the caller strums as before.
 *
 * `notes` is whatever `expandNotes` returned (low→high); `bpm` is the project
 * tempo. Everything is relative to the press: step *k* happens `k * stepSec`
 * seconds after the key went down, and never stops on its own — the caller
 * stops it on key release.
 */
export function arpPlan(notes: number[], s: HarmonySettings, bpm: number): ArpPlan | null {
  if (!arpEnabled(s) || notes.length === 0) return null;
  const beats = ARP_BEATS[s.arp];
  if (!beats) return null;
  const stepSec = (60 / Math.max(1, bpm)) * beats;

  const up = [...notes]; // expandNotes already hands them over low→high
  let order: number[];
  if (s.arpDir === "down") order = [...up].reverse();
  // Up-down turns around *without* repeating the top and bottom notes. With
  // 2 notes or fewer that's the same as plain up, so don't bother.
  else if (s.arpDir === "updown" && up.length > 2) order = [...up, ...up.slice(1, -1).reverse()];
  else order = up;

  return { order, stepSec, gateSec: stepSec * ARP_GATE };
}

/** Human-readable summary of what a key press will do — for the readout. */
export function describe(s: HarmonySettings): string {
  if (s.mode === "note") return "1 note";
  const arp = arpEnabled(s)
    ? ` · arp ${s.arp} ${ARP_DIR_LABELS.find((d) => d.value === s.arpDir)?.label ?? s.arpDir}`
    : "";
  if (s.mode === "harm") return `${s.partials} partials${s.oddOnly ? " · odd" : ""}${arp}`;
  const [kind, name] = s.chord.split(":");
  const label = CHORD_LABELS.find((c) => c.value === s.chord)?.label ?? name;
  const base = kind === "scale" ? `${NOTE_NAMES[s.root]} ${label}${s.seventh ? " 7th" : ""}` : label;
  return base + arp;
}
